import { rufe } from './client';

/**
 * Die Terminoption zwischen Vertrieb und Disposition (ANG-30 bis ANG-38).
 *
 * ============================================================================
 * WARUM DER VERTRIEB NICHT SELBST VORMERKT
 * ============================================================================
 *
 * ANG-32: „Eine Zusage erzeugt die Angebotsvormerkung nach KAP-02 … in einem
 * Vorgang — der Vertrieb setzt keine Vormerkung selbst."
 *
 * Der Grund ist der Betrieb, nicht die Ordnung: Wer verkauft, sieht nicht,
 * was am Tag daneben schon läuft. Die Disposition sieht es. Deshalb ist der
 * Vorgang zweiseitig, und deshalb hat jede Seite ihre Frist — die Disposition
 * eine Antwortfrist (ANG-37), die Zusage eine Optionsfrist (ANG-33).
 */

export type Optionsantwort = 'zugesagt' | 'abgelehnt' | 'vorschlag';

export type Optionsalternative = {
  readonly id: string;
  readonly reihenfolge: number;
  readonly wunschtag: string;
  /** Die Uhrzeit, die der Kunde nannte — `null` heißt „egal" (ANG-30). */
  readonly wunschzeit: string | null;
  /** Der Termin im Kalender; er entsteht mit der Anfrage (0198). */
  readonly termin: string | null;
  readonly terminbeginn: string | null;
  readonly antwort: Optionsantwort | null;
  readonly grund: string | null;
  readonly vorschlagTag: string | null;
  /** Die Optionsfrist der Zusage — sie steht an der Platzhaltung (ANG-33). */
  readonly fristBis: string | null;
  readonly zustaendiger: string | null;
  readonly zustaendigername: string | null;
  readonly beantwortetAm: string | null;
  readonly beantwortername: string | null;
  readonly verlaengertAm: string | null;
  readonly verlaengertername: string | null;
  readonly verlaengerungsgrund: string | null;
};

export type Optionsanfrage = {
  readonly id: string;
  readonly angebot: string;
  readonly angebotsnummer: string;
  readonly position: string | null;
  readonly artikel: string | null;
  readonly leistung: string;
  readonly teilnehmerzahl: number;
  readonly standort: string | null;
  readonly zusatzleistungen: string | null;
  readonly gewuenschteFristBis: string | null;
  readonly anfragender: string;
  readonly anfragendername: string;
  readonly eingegangenAm: string;
  readonly antwortfristBis: string | null;
  readonly erledigtAm: string | null;
  readonly bemerkung: string | null;
  /**
   * ANG-39: die Alternative, die mit der Annahme verbindlich wurde.
   *
   * Gewählt hat sie der Vertrieb bei der Annahme; die übrigen zugesagten sind
   * damit verfallen und haben ihre Plätze freigegeben.
   */
  readonly gewaehlteAlternative: string | null;
  readonly gewaehltAm: string | null;
  readonly gewaehltername: string | null;
  readonly alternativen: readonly Optionsalternative[];
};

/**
 * Ein Wunschtag der Arbeitszeile mit Antwortstand und Standort (Auftrag CRM-69).
 *
 * Den Standort rechnet der Server — aus Anfrage, Termin oder Produkt. `null`
 * heißt: Keiner steht fest, die Planung nimmt den Hauptstandort.
 */
export type Optionswunschtag = {
  readonly tag: string;
  readonly offen: boolean;
  readonly standort: string | null;
};

export type Optionsarbeitszeile = {
  readonly anfrage: string;
  readonly angebot: string;
  readonly angebotsnummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly leistung: string;
  readonly teilnehmerzahl: number;
  readonly standort: string | null;
  readonly wunschtage: readonly string[];
  /** Dieselben Wünsche in derselben Reihenfolge — führen in die Trainingsplanung. */
  readonly wuensche: readonly Optionswunschtag[];
  readonly alternativen: number;
  readonly offene: number;
  readonly anfragender: string;
  readonly eingegangenAm: string;
  readonly antwortfristBis: string | null;
  /** Negativ, wenn die Frist verstrichen ist. `null`, solange keine gesetzt ist. */
  readonly restminuten: number | null;
  readonly eskaliert: boolean;
};

/** ANG-38: Kein Vorgang verschwindet daraus, bevor er beantwortet ist. */
export async function arbeitsliste(): Promise<readonly Optionsarbeitszeile[]> {
  return rufe('/optionsanfragen');
}

export async function eine(id: string): Promise<Optionsanfrage> {
  return rufe(`/optionsanfragen/${encodeURIComponent(id)}`);
}

export async function zumAngebot(angebot: string): Promise<readonly Optionsanfrage[]> {
  return rufe(`/angebote/${encodeURIComponent(angebot)}/optionsanfragen`);
}

export async function anlegen(
  angebot: string,
  eingabe: {
    readonly artikel?: string | null;
    readonly leistung?: string | null;
    readonly teilnehmerzahl: number;
    readonly standort?: string | null;
    readonly zusatzleistungen?: string | null;
    readonly gewuenschteFristBis?: string | null;
    readonly bemerkung?: string | null;
    readonly position?: string | null;
    readonly wunschtermine: readonly { readonly tag: string; readonly zeit: string | null }[];
  },
): Promise<{ readonly id: string }> {
  return rufe(`/angebote/${encodeURIComponent(angebot)}/optionsanfragen`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

export async function zusagen(
  alternative: string,
  /** `termin: null` bestätigt den, der mit dem Wunsch entstand (0198). */
  eingabe: { readonly termin: string | null; readonly fristBis: string | null },
): Promise<{ readonly haltung: string; readonly fristBis: string }> {
  return rufe(`/optionsalternativen/${encodeURIComponent(alternative)}/zusage`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

/** ANG-42: Eine Ablehnung trägt einen Grund — und, wo möglich, einen Vorschlag. */
export async function ablehnen(
  alternative: string,
  grund: string,
  vorschlagTag: string | null,
): Promise<void> {
  await rufe(`/optionsalternativen/${encodeURIComponent(alternative)}/ablehnung`, {
    verfahren: 'POST',
    rumpf: { grund, vorschlagTag },
  });
}

export async function vorschlagen(
  alternative: string,
  vorschlagTag: string,
  grund: string | null,
): Promise<void> {
  await rufe(`/optionsalternativen/${encodeURIComponent(alternative)}/vorschlag`, {
    verfahren: 'POST',
    rumpf: { vorschlagTag, grund },
  });
}

/** ANG-35: Verlängern geht nur mit Grund — er steht später im Vorgang. */
export async function verlaengern(
  alternative: string,
  fristBis: string,
  grund: string,
): Promise<void> {
  await rufe(`/optionsalternativen/${encodeURIComponent(alternative)}/verlaengerung`, {
    verfahren: 'POST',
    rumpf: { fristBis, grund },
  });
}
